/* eslint-disable @typescript-eslint/indent */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/jsx-indent */
import React from 'react';
import Currency from '@components/common/Currrency/Currency';
import { getProductPriceForGuestType } from '@utils/fairPricing';
import Product from '@customTypes/product';
import { isProductUnAvailable } from '@helpers/product';
import HotelDetails from '@customTypes/hotel-details';
import useHotSpot from '@hooks/useHotSpot';
import { FAIR_PRICING_VARIANT } from '@constants/fairPricing';
import { CartItemDetails } from '@customTypes/cart-items';
import useIsMobile from '@hooks/useIsMobile';

type ProductDetailsPriceWithSTPProps = {
  cartData: CartItemDetails | undefined;
  date: Date | undefined;
  hotelDetails: HotelDetails;
  personType: 'adult' | 'child';
  product: Product;
  showFairPricing: boolean;
  timeSlotDesign?: boolean;
  individualBilling?: boolean;
};

function StrikeThroughPrice({
  isHotSpot = false,
  isPricingMarkdown,
  product,
  personType,
  showFairPricing,
  hotelDetails,
  isUnavailable,
}: {
  isHotSpot: boolean | undefined;
  isPricingMarkdown: boolean;
  product: Product;
  personType: 'adult' | 'child';
  showFairPricing: boolean;
  hotelDetails: HotelDetails;
  isUnavailable: boolean;
}) {
  const isMobile = useIsMobile();

  const getHotSpotValue = () => {
    if (hotelDetails.hot_spot) {
      return getProductPriceForGuestType(product, personType, showFairPricing);
    }

    return 0;
  };

  const getPricingMarkdownValue = () => {
    const isAdult = personType === 'adult';

    const isStrikethroughPricing = isAdult
      ? product.is_adult_strikethrough_pricing
      : product.is_child_strikethrough_pricing;

    const maxPrice = isAdult ? product.adult_max_price : product.children_max_price;

    const price =
      isStrikethroughPricing &&
      maxPrice &&
      maxPrice > +product[isAdult ? 'adult_price' : 'children_price']
        ? maxPrice
        : 0;

    const fairPricingMultiplier = 0.0875;
    return showFairPricing ? price * (1 + fairPricingMultiplier) : price;
  };

  const discount =
    product[personType === 'adult' ? 'adult_discount_percentage' : 'children_discount_percentage'];

  return isHotSpot || (isPricingMarkdown && discount >= 0.1) ? (
    <Currency
      value={isHotSpot ? getHotSpotValue() : getPricingMarkdownValue()}
      valueClasses={`uppercase line-through  ${
        isMobile ? 'leading-14px text-sm' : 'leading-4 text-base'
      } ${isUnavailable ? 'text-rp-text-disabled hidden' : 'text-rp-disabled'}`}
      isoCodeClasses="hidden"
    />
  ) : null;
}

export default function ProductDetailsPriceWithSTP({
  cartData,
  date,
  hotelDetails,
  personType,
  product,
  showFairPricing,
  timeSlotDesign = false,
  individualBilling = false,
}: ProductDetailsPriceWithSTPProps) {
  const isMobile = useIsMobile();
  const { applyHotSpotDiscountToTotal, isDateWithinVisitWindow } = useHotSpot();

  const isHotSpot =
    date &&
    product.is_hot_spot &&
    hotelDetails.hot_spot &&
    isDateWithinVisitWindow(date, hotelDetails);

  const isUnAvailable = isProductUnAvailable(product, cartData, hotelDetails, date);

  return (
    <div className="flex flex-col">
      <div
        className={`flex text-21 font-normal font-rp-pn-regular ${
          isUnAvailable ? 'text-rp-text-disabled' : 'text-custom-black'
        } ${timeSlotDesign && !individualBilling ? ' flex-row-reverse' : ' flex-row'}`}
      >
        <div>
          <Currency
            value={
              isHotSpot && hotelDetails.hot_spot
                ? applyHotSpotDiscountToTotal(
                    personType === 'adult' ? +product.adult_price : +product.children_price,
                    hotelDetails.hot_spot,
                    showFairPricing,
                  )
                : getProductPriceForGuestType(product, personType, showFairPricing)
            }
            valueClasses="uppercase leading-4 text-lg leading-18px d:text-xl d:leading-20px"
            isoCodeClasses="text-sm font-rp-pn-regular"
          />
        </div>

        <div className={`${timeSlotDesign && !individualBilling ? 'mr-0.5' : 'ml-0.5'}`}>
          <StrikeThroughPrice
            isHotSpot={isHotSpot}
            hotelDetails={hotelDetails}
            showFairPricing={showFairPricing}
            product={product}
            personType={personType}
            isUnavailable={isUnAvailable}
            isPricingMarkdown={
              personType === 'adult'
                ? product.is_adult_strikethrough_pricing
                : product.is_child_strikethrough_pricing
            }
          />
        </div>
      </div>

      {/* Fair Pricing - Only Desktop */}
      {!isMobile ? (
        !(date && isUnAvailable) && showFairPricing ? (
          <p
            className={`flex-shrink-0 text-12 text-rp-disabled whitespace-nowrap ${
              isMobile ? 'font-medium font-rp-pn-regular leading-11px' : ''
            } ${timeSlotDesign ? 'self-end' : ''}`}
          >
            {FAIR_PRICING_VARIANT === 'variant1'
              ? 'Includes fees'
              : `+$${(+getProductPriceForGuestType(product, personType, showFairPricing)).toFixed(
                  0,
                )} in fees`}
          </p>
        ) : null
      ) : null}
    </div>
  );
}
